import { apiClient } from '@copro/api';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  EventForAgenda,
  FormatMeetingAgendaParams,
  OpenSubjectForAgenda,
  QuoteForAgenda,
  ReminderForAgenda,
} from '../types';

export function formatBasicMeetingHeader(params: {
  title?: string;
  meetingDate?: string | Date;
}): string {
  const rawTitle = params.title?.trim();
  const title = !rawTitle || rawTitle === 'Réunion CS' ? 'Réunion conseil syndical' : rawTitle;

  let formattedDate = '';
  if (params.meetingDate) {
    try {
      const dateObj =
        typeof params.meetingDate === 'string'
          ? parseISO(params.meetingDate)
          : params.meetingDate;
      if (!isNaN(dateObj.getTime())) {
        formattedDate = format(dateObj, "d MMMM yyyy 'à' HH'h'mm", { locale: fr });
      }
    } catch {
      formattedDate = '';
    }
  }

  const h1Text = formattedDate ? `${title} du ${formattedDate}` : title;

  return `<h1 class="ql-align-center" style="text-align: center;">${h1Text}</h1><h2>Ordre du jour</h2>`;
}

export function formatMeetingAgenda(params: FormatMeetingAgendaParams): string {
  let html = formatBasicMeetingHeader({
    title: params.title,
    meetingDate: params.meetingDate,
  });

  const openSubjectIds = new Set(params.openSubjects.map((s) => s.id));
  const quotes = params.quotes ?? [];

  if (params.openSubjects.length === 0) {
    html += `<br><h3>Divers</h3><p><i>Faire un point sur le sujet</i></p>`;
  } else {
    for (const subject of params.openSubjects) {
      html += `<br><h3>${subject.name}</h3>`;

      let hasAnyItem = false;

      // Pending items
      const pendingItems = subject.pendingItems.filter(
        (pi) => !pi.state || pi.state === 'pending'
      );
      if (pendingItems.length > 0) {
        hasAnyItem = true;
        html += `<h4>Points en attente</h4><ul>`;
        for (const item of pendingItems) {
          const provider = item.provider_name ? `${item.provider_name} - ` : '';
          html += `<li>${provider}${item.title}</li>`;
        }
        html += `</ul>`;
      }

      // Quotes pending decision
      const subjectQuotes = quotes.filter(
        (q) => q.subject_id === subject.id && (!q.status || q.status === 'pending')
      );
      if (subjectQuotes.length > 0) {
        hasAnyItem = true;
        html += `<h4>Devis en attente</h4><ul>`;
        for (const quote of subjectQuotes) {
          const provider = quote.provider_name ? `${quote.provider_name} - ` : '';
          const title = quote.title?.trim() || 'Devis';
          const amountStr =
            typeof quote.amount === 'number'
              ? ` (${quote.amount.toLocaleString('fr-FR')} €)`
              : '';
          html += `<li>${provider}${title}${amountStr}</li>`;
        }
        html += `</ul>`;
      }

      // Tasks
      const tasks = subject.tasks.filter((t) => !t.state || t.state === 'pending');
      if (tasks.length > 0) {
        hasAnyItem = true;
        html += `<h4>Tâches</h4><ul>`;
        for (const task of tasks) {
          html += `<li>${task.title}</li>`;
        }
        html += `</ul>`;
      }

      // Events linked to this subject
      const subjectEvents = params.events.filter((e) => e.subject_id === subject.id);
      if (subjectEvents.length > 0) {
        hasAnyItem = true;
        html += `<h4>Rendez-vous</h4><ul>`;
        for (const event of subjectEvents) {
          let dateText: string;
          try {
            const startDateObj = parseISO(event.date);
            if (event.end_date) {
              const endDateObj = parseISO(event.end_date);
              const startFormatted = format(startDateObj, 'dd/MM/yyyy');
              const endFormatted = format(endDateObj, 'dd/MM/yyyy');
              dateText =
                startFormatted === endFormatted
                  ? `le ${startFormatted}`
                  : `du ${startFormatted} au ${endFormatted}`;
            } else {
              dateText = `le ${format(startDateObj, 'dd/MM/yyyy')}`;
            }
          } catch {
            dateText = `le ${event.date}`;
          }
          html += `<li>${event.title} (${dateText})</li>`;
        }
        html += `</ul>`;
      }

      // Reminders linked to this subject
      const subjectReminders = params.reminders.filter((r) => r.subject_id === subject.id);
      if (subjectReminders.length > 0) {
        hasAnyItem = true;
        html += `<h4>Rappels</h4><ul>`;
        for (const reminder of subjectReminders) {
          let dueDateText: string;
          try {
            dueDateText = format(parseISO(reminder.due_date), 'dd/MM/yyyy');
          } catch {
            dueDateText = reminder.due_date;
          }
          html += `<li>${reminder.title} (échéance le ${dueDateText})</li>`;
        }
        html += `</ul>`;
      }

      if (!hasAnyItem) {
        html += `<p><i>Faire un point sur le sujet</i></p>`;
      }
    }
  }

  // Quotes, Events & Reminders not associated with any open subject
  const otherQuotes = quotes.filter(
    (q) =>
      (!q.subject_id || !openSubjectIds.has(q.subject_id)) &&
      (!q.status || q.status === 'pending')
  );
  const otherEvents = params.events.filter(
    (e) => !e.subject_id || !openSubjectIds.has(e.subject_id)
  );
  const otherReminders = params.reminders.filter(
    (r) => !r.subject_id || !openSubjectIds.has(r.subject_id)
  );

  if (otherQuotes.length > 0 || otherEvents.length > 0 || otherReminders.length > 0) {
    let generalHeading = 'Rendez-vous et rappels généraux';
    if (otherQuotes.length > 0) {
      if (otherEvents.length === 0 && otherReminders.length === 0) {
        generalHeading = 'Devis généraux';
      } else {
        generalHeading = 'Devis, rendez-vous et rappels généraux';
      }
    }
    html += `<br><h3>${generalHeading}</h3>`;

    if (otherQuotes.length > 0) {
      html += `<h4>Devis en attente</h4><ul>`;
      for (const quote of otherQuotes) {
        const provider = quote.provider_name ? `${quote.provider_name} - ` : '';
        const title = quote.title?.trim() || 'Devis';
        const amountStr =
          typeof quote.amount === 'number'
            ? ` (${quote.amount.toLocaleString('fr-FR')} €)`
            : '';
        html += `<li>${provider}${title}${amountStr}</li>`;
      }
      html += `</ul>`;
    }

    if (otherEvents.length > 0) {
      html += `<h4>Rendez-vous</h4><ul>`;
      for (const event of otherEvents) {
        let dateText: string;
        try {
          const startDateObj = parseISO(event.date);
          dateText = `le ${format(startDateObj, 'dd/MM/yyyy')}`;
        } catch {
          dateText = `le ${event.date}`;
        }
        html += `<li>${event.title} (${dateText})</li>`;
      }
      html += `</ul>`;
    }

    if (otherReminders.length > 0) {
      html += `<h4>Rappels</h4><ul>`;
      for (const reminder of otherReminders) {
        let dueDateText: string;
        try {
          dueDateText = format(parseISO(reminder.due_date), 'dd/MM/yyyy');
        } catch {
          dueDateText = reminder.due_date;
        }
        html += `<li>${reminder.title} (échéance le ${dueDateText})</li>`;
      }
      html += `</ul>`;
    }
  }

  return html;
}

export type FetchAgendaDataParams = {
  spaceId: string;
  meetingDate?: string;
};

export async function fetchAgendaData(params: FetchAgendaDataParams) {
  const { spaceId, meetingDate } = params;
  const targetDate = meetingDate || new Date().toISOString();

  // 1. Find last council meeting in this space before targetDate
  const { data: pastMeetings } = await apiClient
    .from('council_meetings')
    .select('id, start_time')
    .eq('space_id', spaceId)
    .lt('start_time', targetDate)
    .order('start_time', { ascending: false })
    .limit(1);

  const lastMeetingDate = pastMeetings?.[0]?.start_time ?? null;

  // 2. Fetch open subjects in this space with their tasks and pending items
  const { data: subTopicsData, error: subTopicsError } = await apiClient
    .from('sub_topics')
    .select(`
      id,
      name,
      topics!inner(id, space_id),
      subject_tasks(id, title, state, position),
      subject_pending_items(id, title, state, position, provider_name)
    `)
    .eq('topics.space_id', spaceId)
    .eq('state', 'pending')
    .is('archived_at', null)
    .order('name');

  if (subTopicsError) {
    throw subTopicsError;
  }

  type RawSubTopic = {
    id: string;
    name: string;
    subject_tasks: Array<{ id: number; title: string; state: string; position: number }> | null;
    subject_pending_items: Array<{
      id: number;
      title: string;
      state: string;
      position: number;
      provider_name: string | null;
    }> | null;
  };

  const rawSubTopics = (subTopicsData ?? []) as unknown as RawSubTopic[];

  const openSubjects: OpenSubjectForAgenda[] = rawSubTopics.map((st) => ({
    id: st.id,
    name: st.name,
    tasks: (st.subject_tasks ?? [])
      .filter((t) => t.state === 'pending')
      .sort((a, b) => a.position - b.position),
    pendingItems: (st.subject_pending_items ?? [])
      .filter((pi) => pi.state === 'pending')
      .sort((a, b) => a.position - b.position),
  }));

  // 3. Fetch events in this space
  let eventsQuery = apiClient
    .from('events')
    .select('id, title, date, end_date, subject_id, sub_topics!inner(id, topics!inner(space_id))')
    .eq('sub_topics.topics.space_id', spaceId)
    .order('date', { ascending: true });

  if (lastMeetingDate) {
    eventsQuery = eventsQuery.or(
      `end_date.gte.${lastMeetingDate},and(end_date.is.null,date.gte.${lastMeetingDate})`
    );
  }

  const { data: eventsData, error: eventsError } = await eventsQuery;
  if (eventsError) {
    throw eventsError;
  }

  type RawEvent = {
    id: string;
    title: string;
    date: string;
    end_date: string | null;
    subject_id: string;
  };

  const rawEvents = (eventsData ?? []) as unknown as RawEvent[];

  const events: EventForAgenda[] = rawEvents.map((e) => ({
    id: e.id,
    title: e.title,
    date: e.date,
    end_date: e.end_date,
    subject_id: e.subject_id,
  }));

  // 4. Fetch reminders in this space
  let remindersQuery = apiClient
    .from('reminders')
    .select('id, title, due_date, subject_id, is_completed, space_id')
    .eq('space_id', spaceId)
    .order('due_date', { ascending: true });

  if (lastMeetingDate) {
    remindersQuery = remindersQuery.or(`due_date.gte.${lastMeetingDate},is_completed.eq.false`);
  }

  const { data: remindersData, error: remindersError } = await remindersQuery;
  if (remindersError) {
    throw remindersError;
  }

  type RawReminder = {
    id: string;
    title: string;
    due_date: string;
    subject_id: string | null;
    is_completed: boolean | null;
  };

  const rawReminders = (remindersData ?? []) as unknown as RawReminder[];

  const reminders: ReminderForAgenda[] = rawReminders.map((r) => ({
    id: r.id,
    title: r.title,
    due_date: r.due_date,
    subject_id: r.subject_id,
    is_completed: r.is_completed,
  }));

  // 5. Fetch quotes pending decision in this space
  const { data: quotesData, error: quotesError } = await apiClient
    .from('subject_quotes')
    .select('id, title, amount, provider_name, subject_id, status, providers(name)')
    .eq('space_id', spaceId)
    .eq('status', 'pending');

  if (quotesError) {
    throw quotesError;
  }

  type RawQuote = {
    id: string;
    title: string | null;
    amount: number | null;
    provider_name: string | null;
    subject_id: string | null;
    status: string;
    providers?: { name: string } | null;
  };

  const rawQuotes = (quotesData ?? []) as unknown as RawQuote[];

  const quotes: QuoteForAgenda[] = rawQuotes.map((q) => ({
    id: q.id,
    title: q.title,
    amount: q.amount,
    provider_name: q.provider_name ?? q.providers?.name ?? null,
    subject_id: q.subject_id,
    status: q.status,
  }));

  return {
    openSubjects,
    events,
    reminders,
    quotes,
    lastMeetingDate,
  };
}

export async function generateMeetingAgenda(params: {
  spaceId: string;
  title?: string;
  startTime?: string;
}): Promise<string> {
  const data = await fetchAgendaData({
    spaceId: params.spaceId,
    meetingDate: params.startTime,
  });

  return formatMeetingAgenda({
    title: params.title,
    meetingDate: params.startTime,
    openSubjects: data.openSubjects,
    events: data.events,
    reminders: data.reminders,
    quotes: data.quotes,
    lastMeetingDate: data.lastMeetingDate,
  });
}
